import React, { useState } from 'react';
import { Table, Tag, Button, Modal, Form, Input, Select, Space, message, Card } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, AppstoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useListServicesQuery, useAddServiceMutation, useEditServiceMutation, useDeleteServiceMutation, useListServersQuery } from '../store/api';

const { Option } = Select;

const Servicios = () => {
  const { data = [], isLoading } = useListServicesQuery();
  const { data: servers = [] } = useListServersQuery();
  const [addService] = useAddServiceMutation();
  const [editService] = useEditServiceMutation();
  const [deleteService] = useDeleteServiceMutation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const showModal = (record = null) => {
    setEditingRecord(record);
    record ? form.setFieldsValue(record) : form.resetFields();
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord?.id_servicio) {
        await editService({ id_servicio: editingRecord.id_servicio, ...values }).unwrap();
        message.success('Servicio actualizado');
      } else {
        await addService(values).unwrap();
        message.success('Servicio creado');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      message.error('Error al guardar servicio');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '¿Eliminar servicio?', content: 'Esta acción no se puede deshacer.',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteService(id).unwrap();
          message.success('Servicio eliminado');
        } catch { message.error('Error al eliminar'); }
      },
    });
  };

  const getServerName = (id) => servers.find(s => s.id === id)?.nombre || id;

  const columns = [
    { title: 'ID', dataIndex: 'id_servicio', key: 'id_servicio', width: 60 },
    { title: 'Servicio', dataIndex: 'name_servicio', key: 'name_servicio', render: (t) => <span style={{ fontWeight: 600 }}>{t}</span> },
    { title: 'Descripción', dataIndex: 'desc_servicio', key: 'desc_servicio', ellipsis: true },
    { title: 'Servidor', dataIndex: 'id_servidor', key: 'id_servidor', render: id => getServerName(id) },
    { title: 'Estado', key: 'estado_servidor', render: (_, record) => {
        const server = servers.find(s => String(s.id) === String(record.id_servidor));
        if (!server) return <Tag style={{ borderRadius: 12 }}>N/A</Tag>;
        const isOffline = !server.fechaupdatedsincro || dayjs().diff(dayjs(server.fechaupdatedsincro), 'minute') > 5;
        return <Tag color={isOffline ? 'red' : 'green'} style={{ borderRadius: 12 }}>{isOffline ? 'Offline' : 'Online'}</Tag>;
    }},
    { title: 'Precio', dataIndex: 'prec_servicio', key: 'prec_servicio', render: v => v ? <span style={{ fontWeight: 600 }}>${v}</span> : '-' },
    { title: 'Acciones', key: 'acciones', render: (_, record) => (
        <Space size={4}>
          <button className="action-btn" title="Editar" onClick={() => showModal(record)}><EditOutlined /></button>
          <button className="action-btn" title="Eliminar" onClick={() => handleDelete(record.id_servicio)} style={{ color: '#dc3545' }}><DeleteOutlined /></button>
        </Space>
    )},
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><AppstoreOutlined style={{ marginRight: 8 }} />Servicios</h1>
          <div className="page-subtitle">{data.length} servicios registrados</div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal(null)}
          style={{ borderRadius: 8, fontWeight: 600, background: 'linear-gradient(135deg, #0168fa, #042893)', border: 'none' }}>
          Nuevo Servicio
        </Button>
      </div>

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <div className="modern-table">
          <Table columns={columns} dataSource={data} rowKey="id_servicio" loading={isLoading}
            pagination={{ pageSize: 10, showTotal: t => `${t} servicios` }} scroll={{ x: true }} size="middle" />
        </div>
      </Card>

      <Modal title={editingRecord ? 'Editar Servicio' : 'Nuevo Servicio'} open={isModalVisible}
        onOk={handleSave} onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
        okText="Guardar" cancelText="Cancelar">
        <Form form={form} layout="vertical">
          <Form.Item name="name_servicio" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre' }]}><Input /></Form.Item>
          <Form.Item name="desc_servicio" label="Descripción"><Input.TextArea /></Form.Item>
          <Form.Item name="id_servidor" label="Servidor">
            <Select showSearch optionFilterProp="children" placeholder="Servidor (opcional)">
              <Option value={null}>N/A</Option>
              {servers.map(srv => <Option key={srv.id} value={srv.id}>{srv.nombre}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="prec_servicio" label="Precio"><Input type="number" prefix="$" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Servicios;
